(() => {
  if (window.FormeCategoryStrip) {
    window.FormeCategoryStrip.initAll();
    return;
  }

  const controllers = new Map();
  const desktopQuery = window.matchMedia('(min-width: 768px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CategoryStripController {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-category-strip-viewport]');
      this.track = root.querySelector('[data-category-strip-track]');
      this.previous = root.querySelector('[data-category-strip-previous]');
      this.next = root.querySelector('[data-category-strip-next]');
      this.abortController = new AbortController();
      this.resizeObserver = null;

      if (!this.viewport || !this.track) return;

      const options = { signal: this.abortController.signal };
      this.previous?.addEventListener('click', () => this.scrollByItem(-1), options);
      this.next?.addEventListener('click', () => this.scrollByItem(1), options);
      this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
      this.track.addEventListener('focusin', (event) => this.reveal(event.target), options);
      desktopQuery.addEventListener('change', () => this.updateControls(), options);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      this.updateControls();
    }

    scrollByItem(direction) {
      if (!desktopQuery.matches) return;
      const item = this.track.querySelector('[data-category-strip-item]');
      if (!item) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const rtlFactor = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({
        left: direction * rtlFactor * (item.getBoundingClientRect().width + gap),
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    reveal(target) {
      const item = target?.closest?.('[data-category-strip-item]');
      if (!item || !this.root.contains(item)) return;
      item.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    updateControls() {
      if (!this.previous || !this.next) return;

      const items = this.track.querySelectorAll('[data-category-strip-item]');
      const first = items[0];
      const last = items[items.length - 1];
      const hasOverflow = desktopQuery.matches && this.track.scrollWidth > this.track.clientWidth + 1;

      this.previous.hidden = !hasOverflow;
      this.next.hidden = !hasOverflow;
      if (!hasOverflow || !first || !last) return;

      const trackRect = this.track.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const isRtl = getComputedStyle(this.track).direction === 'rtl';
      const atStart = isRtl
        ? firstRect.right <= trackRect.right + 1
        : firstRect.left >= trackRect.left - 1;
      const atEnd = isRtl
        ? lastRect.left >= trackRect.left - 1
        : lastRect.right <= trackRect.right + 1;

      this.previous.disabled = atStart;
      this.next.disabled = atEnd;
    }

    destroy() {
      this.abortController.abort();
      this.resizeObserver?.disconnect();
    }
  }

  const initRoot = (root) => {
    if (!root?.id) return;
    controllers.get(root.id)?.destroy();
    controllers.set(root.id, new CategoryStripController(root));
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-category-strip]')) initRoot(scope);
    scope.querySelectorAll?.('[data-category-strip]').forEach(initRoot);
  };

  const unload = (root) => {
    if (!root?.id) return;
    controllers.get(root.id)?.destroy();
    controllers.delete(root.id);
  };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target.querySelector?.('[data-category-strip]') || event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-category-strip]');
    controllers.get(root?.id)?.reveal(event.target);
  });

  window.FormeCategoryStrip = { initAll };
  initAll();
})();
